import { Layer, Source } from 'react-map-gl';
import type { MultiPolygon, Polygon } from 'geojson';
import type { ReferenceArea } from './types';
import { REFERENCE_AREA_COLOR } from './utils';

const SOURCE = 'area-edit-reference';
/** The reference fills, which snapping hit-tests for boundaries. */
export const REFERENCE_FILL_LAYER = `${SOURCE}-fill`;

interface ReferenceAreasLayerProps {
  areas: ReferenceArea[];
  /** Trace is the tool: the outlines firm up to be followed. */
  tracing: boolean;
}

/**
 * Other areas on the edit map: a faint orange fill under a dashed orange line,
 * drawn heavier while Trace is the tool.
 *
 * @param {ReferenceAreasLayerProps} props - The areas and the tool.
 * @return {JSX.Element} The reference layers.
 */
export function ReferenceAreasLayer({
  areas,
  tracing,
}: ReferenceAreasLayerProps): JSX.Element {
  return (
    <Source
      id={SOURCE}
      type="geojson"
      data={{
        type: 'FeatureCollection',
        features: areas.map((area) => ({
          type: 'Feature',
          properties: { id: area.id, name: area.name },
          geometry: area.geometry,
        })),
      }}
    >
      <Layer
        id={REFERENCE_FILL_LAYER}
        type="fill"
        // Faint: a crowded view stacks many, and the fill is only there to hover
        // and snap; just enough to read the areas apart from the base map.
        paint={{ 'fill-color': REFERENCE_AREA_COLOR, 'fill-opacity': 0.06 }}
      />
      <Layer
        id={`${SOURCE}-line`}
        type="line"
        paint={{
          'line-color': REFERENCE_AREA_COLOR,
          'line-width': tracing ? 2.5 : 1,
          'line-opacity': tracing ? 1 : 0.8,
          'line-dasharray': [2, 2],
        }}
      />
    </Source>
  );
}

interface ReferenceHighlightLayerProps {
  geometries: (Polygon | MultiPolygon)[];
}

/**
 * The reference rows put on the map with Show on map: a solid, heavier orange
 * so they can be found among the other, dashed references.
 *
 * @param {ReferenceHighlightLayerProps} props - The areas' geometries.
 * @return {JSX.Element} The highlight layers.
 */
export function ReferenceHighlightLayer({
  geometries,
}: ReferenceHighlightLayerProps): JSX.Element {
  return (
    <Source
      id={`${SOURCE}-highlight`}
      type="geojson"
      data={{
        type: 'FeatureCollection',
        features: geometries.map((geometry) => ({
          type: 'Feature',
          properties: {},
          geometry,
        })),
      }}
    >
      <Layer
        id={`${SOURCE}-highlight-fill`}
        type="fill"
        paint={{ 'fill-color': REFERENCE_AREA_COLOR, 'fill-opacity': 0.24 }}
      />
      <Layer
        id={`${SOURCE}-highlight-line`}
        type="line"
        paint={{ 'line-color': '#eb6200', 'line-width': 3 }}
      />
    </Source>
  );
}
